import { IconButton } from '@droplet-tech-code/core-elements/module/ui/Button';
import {
  FormBuilder,
  FormHandler,
} from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import { Modal, useModal } from '@droplet-tech-code/core-elements/module/ui/Modal';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { HStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { View } from '@droplet-tech-code/core-elements/module/ui/View';
import {
  BookingClientWithPurchasesAndTickets,
  SupportRequestBackendClient,
  SupportRequestCreateChargeRequest,
} from '@naus-code/naus-admin-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable } from 'react-native';
import { useConversionTool } from '~/components/CurrencyConversion/CurrencyConversionTool';

import { FormContent } from '~/components/Form/Form.Container';
import { Pill } from '~/components/Pill/Pill';
import { AppText } from '~/components/Text';
import { getPriceWithSymbol } from '~/screens/Support/Purchases/purchase.utils';
import { supportRequestActionsApi } from '~/screens/Support/Requests/supportRequestActions.api';
import { handleRTKResponse } from '~/utils/network.utils';
import { translate } from '~/utils/translation.utils';
import { useIsSystemUser } from '~/hooks/auth.hook';

export const CreatePaymentChargeKey = 'AddModal-CreatePayment';
export const CreatePaymentChargeModal = ({
  supportRequestInfo,
  booking,
  check,
}: {
  supportRequestInfo: SupportRequestBackendClient;
  booking?: BookingClientWithPurchasesAndTickets;
  check: string;
}) => {
  const isSystem = useIsSystemUser();
  const form = useRef(new FormHandler());
  const { closeModal, openModal } = useModal();
  const supportId = supportRequestInfo._id;
  const { open: openConversionTool } = useConversionTool();

  const selectedReservation = supportRequestInfo.purchaseId;

  // Live charge/fee values (in customer currency) mirrored from the form so we
  // can show a running total at the bottom of the modal.
  const [amounts, setAmounts] = useState<{ value: number; serviceFee: number }>({
    value: 0,
    serviceFee: 0,
  });
  const customerCurrency = booking?.paymentSessions[0]?.paymentSummary.currency ?? '';

  // Seed the total from the config's initial values once it loads, so an
  // unmodified (but pre-filled) service fee still counts toward the total —
  // the value-change subscription only fires for fields the user edits.
  const { data: paymentChargeConfig } =
    supportRequestActionsApi.useCreatePaymentChargeFormConfigQuery(supportId);
  const didSeedAmounts = useRef(false);
  useEffect(() => {
    if (!paymentChargeConfig || didSeedAmounts.current) return;
    const items = paymentChargeConfig.flatMap((section) =>
      'items' in section ? section.items : [],
    );
    const getInitialNumber = (id: string) => {
      const item = items.find((i) => i.id === id) as
        | { initialValue?: number }
        | undefined;
      const num = Number(item?.initialValue);
      return Number.isNaN(num) ? 0 : num;
    };
    setAmounts({
      value: getInitialNumber('value'),
      serviceFee: getInitialNumber('serviceFee'),
    });
    didSeedAmounts.current = true;
  }, [paymentChargeConfig]);

  const [createPendingPaymentCharge, { isLoading: isCreatingPaymentCharge }] =
    supportRequestActionsApi.useCreatePendingPaymentChargeMutation();

  // The server config now includes companyValue and a readonly companyCcyRate.
  // Entering a company-ccy value auto-fills the customer charge value at that
  // rate; the value field stays independently editable afterwards. Rounded to
  // 2dp — the server rejects sub-cent precision.
  // Number inputs emit raw display text to subscribers, hence the string parse.
  useEffect(() => {
    return form.current.subscribeToValueChange((id, val) => {
      if (id === 'value' || id === 'serviceFee') {
        const num = Number(String(val ?? '').replace(/,/g, ''));
        setAmounts((prev) => ({ ...prev, [id]: Number.isNaN(num) ? 0 : num }));
      }

      if (id !== 'companyValue') return;
      const companyValue = Number(String(val ?? '').replace(/,/g, ''));
      if (Number.isNaN(companyValue)) return;
      const rate =
        Number(
          form.current.getSingleValue({
            id: 'companyCcyRate',
            validate: false,
            showFeedback: false,
          }),
        ) || 1;
      form.current.setInputValue(
        'value',
        String(Math.round(companyValue * rate * 100) / 100),
      );
    });
  }, []);

  const [createBooking, { isLoading: isLoadingBooking }] =
    supportRequestActionsApi.useCreateBookingMutation();

  const onCreatePendingCharge = useCallback(
    async (value) => {
      const payload = {
        ...value,
        value: Math.round((value.value ?? 0) * 100),
        serviceFee: Math.round((value.serviceFee ?? 0) * 100),
        supportId,
        check,
      };
      return await createPendingPaymentCharge(payload);
    },
    [check, supportId, createPendingPaymentCharge],
  );

  const onCreateBooking = useCallback(async () => {
    const res = await createBooking({ supportId, check });
    handleResponse({
      response: res,
      onSuccess: ({ data }) => {
        closeModal(CreatePaymentChargeKey);
        openModal(
          <CreatePaymentChargeModal
            supportRequestInfo={data.supportRequest}
            check={data.check}
          />,
          {
            key: CreatePaymentChargeKey,
            type: 'action-sheet-full',
          },
        );
      },
    });
  }, [check, supportId]);

  // const { data: paymentCreateConfig, isLoading } =
  //   supportRequestActionsApi.useCreatePaymentChargeFormConfigQuery(supportId);

  // if (paymentCreateConfig === undefined) {
  //   return null;
  // }

  //   const { check } = paymentCreateConfig;

  return (
    <Modal.Container>
      <HStack>
        <Modal.Header
          title={translate('supportRequests.createPayment')}
          subtitle={translate('supportRequests.enterPaymentDetails')}
          children={
            !supportRequestInfo.bookingId && isSystem ? (
              <CreateBookingPill
                onCreateBooking={onCreateBooking}
                isLoading={isLoadingBooking}
              />
            ) : null
          }
          onPress={() => {
            closeModal(CreatePaymentChargeKey);
          }}
        />
        <View flex={1} />
        <View p="2.5">
          <IconButton
            icon="transactions"
            color="monochrome"
            disabled={!booking}
            onPress={() => {
              if (!booking) {
                return;
              }
              const companyCcy = booking.purchases.find(
                (purchase) => purchase._id === selectedReservation,
              )?.companyCcy;
              openConversionTool({
                fromCurrency: companyCcy,
                toCurrency: booking.paymentSessions[0].paymentSummary.currency,
              });
            }}
          />
        </View>
      </HStack>
      <Modal.Body p="0">
        <CreatePaymentForm supportId={supportId} form={form.current} />
      </Modal.Body>
      <View px="4" pt="3">
        <HStack space="2" align="center">
          <AppText.Body1Medium color="monochrome-extraDark" flex={1}>
            {translate('supportRequests.total')}
          </AppText.Body1Medium>
          <AppText.Body1Medium>
            {getPriceWithSymbol(
              Math.round((amounts.value + amounts.serviceFee) * 100),
              customerCurrency,
            )}
          </AppText.Body1Medium>
        </HStack>
      </View>
      <Modal.Footer
        divider
        isLoading={isCreatingPaymentCharge}
        onConfirm={async () => {
          const { valid, value } =
            form.current.getValue<SupportRequestCreateChargeRequest>();

          if (valid) {
            const response = await onCreatePendingCharge(value);
            handleRTKResponse({
              response,
              onSuccess: () => {
                closeModal(CreatePaymentChargeKey);
              },
            });
          }
        }}
        onCancel={() => {
          closeModal(CreatePaymentChargeKey);
        }}
      />
    </Modal.Container>
  );
};

export const useCreatePaymentCharge = () => {
  const { openModal } = useModal();

  return {
    openModal: ({
      supportRequestInfo,
      booking,
      check,
    }: {
      supportRequestInfo: SupportRequestBackendClient;
      booking?: BookingClientWithPurchasesAndTickets;
      check: string;
    }) => {
      openModal(
        <CreatePaymentChargeModal
          booking={booking}
          supportRequestInfo={supportRequestInfo}
          check={check}
        />,
        {
          key: CreatePaymentChargeKey,
          type: 'action-sheet-full',
        },
      );
    },
  };
};

const CreatePaymentForm = ({
  supportId,
  form,
}: {
  supportId: string;
  form: FormHandler;
}) => {
  const { data: paymentCreateConfig, isLoading: isLoadingPaymentConfig } =
    supportRequestActionsApi.useCreatePaymentChargeFormConfigQuery(supportId);

  return (
    <FormContent isLoading={isLoadingPaymentConfig} form={paymentCreateConfig} flex={1}>
      {paymentCreateConfig ? (
        <FormBuilder
          data={paymentCreateConfig}
          form={form}
          initialValue={{ description: 'Openferry' }}
        />
      ) : null}
    </FormContent>
  );
};

const CreateBookingPill = ({
  onCreateBooking,
  isLoading,
}: {
  onCreateBooking: () => void;
  isLoading?: boolean;
}) => {
  return (
    <Pressable onPress={onCreateBooking}>
      <Pill color="success-light" isLoading={isLoading}>
        <AppText.MiscTag color="success-light">
          {translate('supportRequests.createBooking')}
        </AppText.MiscTag>
      </Pill>
    </Pressable>
  );
};
